import { useState, useEffect } from "react";
import { api } from "@/lib/api";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Trash2 } from "lucide-react";
import { toast } from "sonner";

/**
 * Confirm "slett alt" with a short numeric PIN. On success, the dialog calls
 * the `deleteAllPantry` API and emits `onDeleted(count)` so the parent can
 * refresh its list + show info about the 30-day trash recovery window.
 */
export default function DeleteAllPantryDialog({ open, onOpenChange, requiredPin, onDeleted }) {
  const [input, setInput] = useState("");
  const pin = requiredPin || "1234";

  useEffect(() => { if (!open) setInput(""); }, [open]);

  const confirm = async () => {
    if (input !== pin) {
      toast.error("Feil kode. Prøv igjen.");
      return;
    }
    try {
      const res = await api.deleteAllPantry(input);
      toast.success(`${res.data.deleted} varer flyttet til papirkurv (gjenopprettbar i ${res.data.recoverable_for_days || 30} dager)`);
      onOpenChange(false);
      setInput("");
      onDeleted?.(res.data.deleted || 0);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Feil");
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-sm" data-testid="delete-all-pin-dialog">
        <DialogHeader>
          <DialogTitle style={{ fontFamily: "'Cormorant Garamond', serif" }}>Bekreft sletting</DialogTitle>
          <DialogDescription>
            Dette flytter <strong>hele beholdningen</strong> til papirkurven. Du kan gjenopprette varene i 30 dager etterpå. Skriv inn slettekoden for å bekrefte.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <input
            autoFocus
            type="password"
            inputMode="numeric"
            pattern="[0-9]*"
            maxLength={8}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => { if (e.key === "Enter") confirm(); }}
            placeholder="••••"
            className="w-full rounded-md border border-stone-200 bg-white px-3 py-3 text-center text-lg font-mono tracking-[0.5em] focus:border-red-500 focus:ring-1 focus:ring-red-500 outline-none"
            data-testid="delete-all-pin-input"
          />
          <div className="flex gap-2">
            <button
              onClick={() => { onOpenChange(false); setInput(""); }}
              className="flex-1 rounded-md border border-stone-200 bg-white px-4 py-2.5 text-sm font-medium text-stone-700 hover:bg-stone-50 transition-colors"
              data-testid="delete-all-cancel-btn"
            >
              Avbryt
            </button>
            <button
              onClick={confirm}
              disabled={input !== pin}
              className="flex-1 inline-flex items-center justify-center gap-2 rounded-md bg-red-600 px-4 py-2.5 text-sm font-semibold text-white hover:bg-red-700 transition-colors disabled:opacity-40 disabled:cursor-not-allowed"
              data-testid="delete-all-confirm-btn"
            >
              <Trash2 className="h-4 w-4" /> Slett alt
            </button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
